"use client";

import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import Link from "next/link";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import ComplianceStatement from "./ComplianceStatement";
import GDPRStatement from "./GDPRStatement";
import { Gavel, Shield } from "lucide-react";

export function ComplianceCard() {
  const t = useTranslations("compliance");
  const locale = useLocale();

  return (
    <Card className="my-2 border-primary shadow-md">
      <CardContent className="p-4">
        <div className="flex items-center mb-2">
          <Gavel className="h-5 w-5 mr-2 text-primary" />
          <h3 className="text-lg font-semibold text-primary">
            {t("complianceTitle")}
          </h3>
        </div>
        <ComplianceStatement />
        <div className="mt-3 mb-2">
          <p className="text-sm font-medium text-muted-foreground">
            {t("certifications")}：
          </p>
          <div className="flex items-center mt-2 space-x-4">
            <div className="relative h-8 w-auto">
              <Image
                src="/iso27001-badge.png"
                alt="ISO 27001"
                width={80}
                height={32}
                className="object-contain"
                unoptimized
              />
            </div>
            <div className="relative h-8 w-auto">
              <Image
                src="/soc2-badge.png"
                alt="SOC 2"
                width={80}
                height={32}
                className="object-contain"
                unoptimized
              />
            </div>
          </div>
        </div>
        <div className="mt-3">
          <Link
            href={`/${locale}/help/compliance-report`}
            className="text-sm text-primary hover:underline"
          >
            {t("downloadReport")}
          </Link>
        </div>
        <Link
          href={`/${locale}/compliance`}
          className="text-sm text-primary hover:underline mt-1 inline-block"
        >
          {t("viewFullStatement")}
        </Link>
      </CardContent>
    </Card>
  );
}

export function GDPRCard() {
  const t = useTranslations("gdpr");
  const locale = useLocale();

  return (
    <Card className="my-2 bg-green-100 border-green-600 shadow-sm dark:bg-green-900/30 dark:border-green-700">
      <CardContent className="p-4">
        <div className="flex items-center mb-2">
          <Shield className="h-5 w-5 mr-2 text-green-600 dark:text-green-500" />
          <h3 className="text-lg font-semibold text-green-800 dark:text-green-300">
            {t("gdprTitle")}
          </h3>
        </div>
        <Alert
          variant="default"
          className="mb-2 bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300"
        >
          <p>{t("euUsersOnly")}</p>
        </Alert>
        <GDPRStatement />
        <Link
          href={`/${locale}/gdpr`}
          className="text-sm text-green-700 hover:underline mt-2 inline-block dark:text-green-400"
        >
          {t("viewFullGdpr")}
        </Link>
      </CardContent>
    </Card>
  );
}
